import type { ValueOf } from '#layers/base/public';
import { isNil } from '#layers/base/public';

type ItemShape = Record<string, any>;

export const FilterOperator = {
	EQ: '_eq',
	NEQ: '_neq',
	CONTAINS: '_contains',
	NCONTAINS: '_ncontains',
	ICONTAINS: '_icontains',
	NICONTAINS: '_nicontains',
	STARTS_WITH: '_starts_with',
	NSTARTS_WITH: '_nstarts_with',
	ENDS_WITH: '_ends_with',
	NENDS_WITH: '_nends_with',
	LT: '_lt',
	LTE: '_lte',
	GT: '_gt',
	GTE: '_gte',
	BETWEEN: '_between',
	NBETWEEN: '_nbetween',
	IN: '_in',
	NIN: '_nin',
	NULL: '_null',
} as const;

export type FilterOperator = ValueOf<typeof FilterOperator>;

/** The filter variable the engine substitutes with the current user's primary key (policy rules only). */
export const CURRENT_USER_VARIABLE = '$CURRENT_USER';
export const CURRENT_USER_OPTION = { id: CURRENT_USER_VARIABLE, labelKey: 'filters.current_user' } as const;

export const FILTER_OPERATORS: FilterOperator[] = Object.values(FilterOperator) as FilterOperator[];

const NON_SCALAR_OPERATORS: Set<FilterOperator> = new Set([
	FilterOperator.IN,
	FilterOperator.NIN,
	FilterOperator.BETWEEN,
	FilterOperator.NBETWEEN,
	FilterOperator.NULL,
]);

const LIST_VALUE_OPERATORS: Set<FilterOperator> = new Set([
	FilterOperator.IN,
	FilterOperator.NIN,
]);

const BETWEEN_VALUE_OPERATORS: Set<FilterOperator> = new Set([
	FilterOperator.BETWEEN,
	FilterOperator.NBETWEEN,
]);

const SINGLE_VALUE_OPERATORS: Set<FilterOperator> = new Set(
	FILTER_OPERATORS.filter((op) => !NON_SCALAR_OPERATORS.has(op)),
);

export function isSingleValueOperator(op: FilterOperator | null | undefined): op is FilterOperator {
	return !isNil(op) && SINGLE_VALUE_OPERATORS.has(op);
}

export function isListValueOperator(op: FilterOperator | null | undefined): op is FilterOperator {
	return !isNil(op) && LIST_VALUE_OPERATORS.has(op);
}

export function isBetweenValueOperator(op: FilterOperator | null | undefined): op is FilterOperator {
	return !isNil(op) && BETWEEN_VALUE_OPERATORS.has(op);
}

export const FilterRelationalQuantifier = {
	Some: '_some',
	Every: '_every',
	None: '_none',
} as const;

export type FilterRelationalQuantifier = ValueOf<typeof FilterRelationalQuantifier>;

export const FILTER_RELATIONAL_QUANTIFIERS: FilterRelationalQuantifier[] = Object.values(FilterRelationalQuantifier) as FilterRelationalQuantifier[];

export type Filter<TItem extends ItemShape = ItemShape> = LogicalFilter<TItem> | UnaryFilterNot<TItem> | FieldFilter<TItem>;

export const LogicalOperator = {
	OR: '_or',
	AND: '_and',
} as const;

export const UnaryFilterOperator = {
	NOT: '_not',
} as const;

export type LogicalOperator = ValueOf<typeof LogicalOperator>;

export type UnaryFilterOperator = ValueOf<typeof UnaryFilterOperator>;

type _LogicalFilter<T extends LogicalOperator, TItem extends ItemShape = ItemShape> = {
	[K in T]: Filter<TItem>[];
};

export type LogicalFilterOR<TItem extends ItemShape = ItemShape> = _LogicalFilter<typeof LogicalOperator.OR, TItem>;
export type LogicalFilterAND<TItem extends ItemShape = ItemShape> = _LogicalFilter<typeof LogicalOperator.AND, TItem>;
export interface UnaryFilterNot<TItem extends ItemShape = ItemShape> {
	_not: Filter<TItem>;
}

export type LogicalFilter<TItem extends ItemShape = ItemShape> = LogicalFilterOR<TItem> | LogicalFilterAND<TItem>;

export type FieldFilter<TItem extends ItemShape | unknown = ItemShape> = {
	[Key in keyof TItem]?: FieldFilterOperator | FieldFilter<TItem[Key]> | TItem[Key];
};

// TODO refine FieldFilterOperator based on ItemShape whenever possible
export interface FieldFilterOperator {
	_eq?: string | number | boolean | null;
	_neq?: string | number | boolean | null;
	_contains?: string;
	_ncontains?: string;
	_icontains?: string;
	_nicontains?: string;
	_starts_with?: string;
	_nstarts_with?: string;
	_ends_with?: string;
	_nends_with?: string;
	_lt?: string | number;
	_lte?: string | number;
	_gt?: string | number;
	_gte?: string | number;
	_between?: (string | number)[];
	_nbetween?: (string | number)[];
	_in?: (string | number)[];
	_nin?: (string | number)[];
	_null?: boolean;
}
